<template>
  <div class="filter-card insurance-filter-card">
    <div class="filter-card-header">
      <div class="filter-card-title">
        <i class="fa fa-shield filter-icon"></i>
        <span>Insurance Filter</span>
      </div>
      <b-dropdown variant="link" size="sm" no-caret right class="filter-menu">
        <template #button-content>
          <i class="fa fa-ellipsis-v text-muted"></i>
        </template>
        <b-dropdown-item @click="$emit('duplicate')">
          <i class="fa fa-copy mr-2"></i>Duplicate
        </b-dropdown-item>
        <b-dropdown-item variant="danger" @click="$emit('remove')">
          <i class="fa fa-trash mr-2"></i>Remove
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="filter-card-body">
      <b-row>
        <!-- Insurance Carrier -->
        <b-col md="4">
          <div class="mb-3">
            <label class="filter-label">Insurance Carrier</label>
            <multiselect
              v-model="selectedCarriers"
              :options="carrierOptions"
              :multiple="true"
              :searchable="true"
              :close-on-select="false"
              :show-labels="false"
              label="text"
              track-by="value"
              placeholder="Search carriers..."
              class="filter-multiselect"
              @input="onCarriersChange"
              @search-change="onCarrierSearch"
            >
              <template slot="beforeList">
                <div
                  v-if="carrierMatchCount >= 3 && carrierSearchQuery.length > 0"
                  class="wildcard-hint"
                  @mousedown.prevent="applyWildcard('carrier')"
                >
                  <i class="fa fa-magic"></i>
                  Select all {{ carrierMatchCount }} matching &mdash; or type <code>{{ carrierSearchQuery }}*</code>
                </div>
              </template>
            </multiselect>
          </div>
        </b-col>

        <!-- Employer -->
        <b-col md="4">
          <div class="mb-3">
            <label class="filter-label">Employer</label>
            <multiselect
              v-model="selectedEmployers"
              :options="employerOptions"
              :multiple="true"
              :searchable="true"
              :close-on-select="false"
              :show-labels="false"
              label="text"
              track-by="value"
              placeholder="Search employers..."
              class="filter-multiselect"
              @input="onEmployersChange"
              @search-change="onEmployerSearch"
            >
              <template slot="beforeList">
                <div
                  v-if="employerMatchCount >= 3 && employerSearchQuery.length > 0"
                  class="wildcard-hint"
                  @mousedown.prevent="applyWildcard('employer')"
                >
                  <i class="fa fa-magic"></i>
                  Select all {{ employerMatchCount }} matching &mdash; or type <code>{{ employerSearchQuery }}*</code>
                </div>
              </template>
            </multiselect>
          </div>
        </b-col>

        <!-- Group Number -->
        <b-col md="4">
          <div class="mb-3">
            <label class="filter-label">Group Number</label>
            <multiselect
              v-model="selectedGroupNumbers"
              :options="groupNumberOptions"
              :multiple="true"
              :searchable="true"
              :close-on-select="false"
              :show-labels="false"
              label="text"
              track-by="value"
              placeholder="Search group numbers..."
              class="filter-multiselect"
              @input="onGroupNumbersChange"
              @search-change="onGroupNumberSearch"
            >
              <template slot="beforeList">
                <div
                  v-if="groupNumberMatchCount >= 3 && groupNumberSearchQuery.length > 0"
                  class="wildcard-hint"
                  @mousedown.prevent="applyWildcard('groupNumber')"
                >
                  <i class="fa fa-magic"></i>
                  Select all {{ groupNumberMatchCount }} matching &mdash; or type <code>{{ groupNumberSearchQuery }}*</code>
                </div>
              </template>
            </multiselect>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'InsuranceFilterCard',
  components: { Multiselect },
  props: {
    value: {
      type: Object,
      required: true
    },
    insuranceOptions: {
      type: Object,
      default: () => ({ carriers: [], employers: [], groupNumbers: [] })
    }
  },
  data() {
    return {
      localFilter: JSON.parse(JSON.stringify(this.value)),
      selectedCarriers: [],
      selectedEmployers: [],
      selectedGroupNumbers: [],
      carrierSearchQuery: '',
      employerSearchQuery: '',
      groupNumberSearchQuery: ''
    };
  },
  computed: {
    carrierOptions() {
      return (this.insuranceOptions.carriers || []).map(c => ({
        value: c.id,
        text: c.name
      }));
    },
    employerOptions() {
      return (this.insuranceOptions.employers || []).map(e => ({
        value: e.id,
        text: e.name
      }));
    },
    groupNumberOptions() {
      return (this.insuranceOptions.groupNumbers || []).map(g => ({
        value: g.value,
        text: g.carrierName ? `${g.value} - ${g.carrierName}` : g.value
      }));
    },
    carrierMatchCount() {
      if (!this.carrierSearchQuery) return 0;
      const q = this.carrierSearchQuery.toUpperCase();
      const selectedValues = new Set(this.selectedCarriers.map(s => s.value));
      return this.carrierOptions.filter(o =>
        !selectedValues.has(o.value) && o.text.toUpperCase().startsWith(q)
      ).length;
    },
    employerMatchCount() {
      if (!this.employerSearchQuery) return 0;
      const q = this.employerSearchQuery.toUpperCase();
      const selectedValues = new Set(this.selectedEmployers.map(s => s.value));
      return this.employerOptions.filter(o =>
        !selectedValues.has(o.value) && o.text.toUpperCase().startsWith(q)
      ).length;
    },
    groupNumberMatchCount() {
      if (!this.groupNumberSearchQuery) return 0;
      const q = this.groupNumberSearchQuery.toUpperCase();
      const selectedValues = new Set(this.selectedGroupNumbers.map(s => s.value));
      return this.groupNumberOptions.filter(o =>
        !selectedValues.has(o.value) && o.text.toUpperCase().startsWith(q)
      ).length;
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.localFilter = JSON.parse(JSON.stringify(newVal));
        this.syncSelections();
      },
      deep: true
    }
  },
  created() {
    this.syncSelections();
  },
  methods: {
    syncSelections() {
      this.selectedCarriers = (this.localFilter.carriers || [])
        .map(id => this.carrierOptions.find(o => o.value === id))
        .filter(Boolean);
      this.selectedEmployers = (this.localFilter.employers || [])
        .map(id => this.employerOptions.find(o => o.value === id))
        .filter(Boolean);
      this.selectedGroupNumbers = (this.localFilter.groupNumbers || [])
        .map(val => this.groupNumberOptions.find(o => o.value === val))
        .filter(Boolean);
    },
    onCarriersChange(selected) {
      this.localFilter.carriers = selected.map(s => s.value);
      this.emitUpdate();
    },
    onEmployersChange(selected) {
      this.localFilter.employers = selected.map(s => s.value);
      this.emitUpdate();
    },
    onGroupNumbersChange(selected) {
      this.localFilter.groupNumbers = selected.map(s => s.value);
      this.emitUpdate();
    },
    onCarrierSearch(query) {
      query = query.trim();
      if (query.indexOf('*') > -1) {
        const prefix = query.substring(0, query.length - 1);
        this.wildcardSelect('carrier', prefix);
        this.carrierSearchQuery = '';
      } else {
        this.carrierSearchQuery = query;
      }
    },
    onEmployerSearch(query) {
      query = query.trim();
      if (query.indexOf('*') > -1) {
        const prefix = query.substring(0, query.length - 1);
        this.wildcardSelect('employer', prefix);
        this.employerSearchQuery = '';
      } else {
        this.employerSearchQuery = query;
      }
    },
    onGroupNumberSearch(query) {
      query = query.trim();
      if (query.indexOf('*') > -1) {
        const prefix = query.substring(0, query.length - 1);
        this.wildcardSelect('groupNumber', prefix);
        this.groupNumberSearchQuery = '';
      } else {
        this.groupNumberSearchQuery = query;
      }
    },
    wildcardSelect(field, prefix) {
      const upper = prefix.toUpperCase();
      if (field === 'carrier') {
        const selectedValues = new Set(this.selectedCarriers.map(s => s.value));
        const matches = this.carrierOptions.filter(o =>
          !selectedValues.has(o.value) && o.text.toUpperCase().startsWith(upper)
        );
        this.selectedCarriers = [...this.selectedCarriers, ...matches];
        this.onCarriersChange(this.selectedCarriers);
      } else if (field === 'employer') {
        const selectedValues = new Set(this.selectedEmployers.map(s => s.value));
        const matches = this.employerOptions.filter(o =>
          !selectedValues.has(o.value) && o.text.toUpperCase().startsWith(upper)
        );
        this.selectedEmployers = [...this.selectedEmployers, ...matches];
        this.onEmployersChange(this.selectedEmployers);
      } else if (field === 'groupNumber') {
        const selectedValues = new Set(this.selectedGroupNumbers.map(s => s.value));
        const matches = this.groupNumberOptions.filter(o =>
          !selectedValues.has(o.value) && o.text.toUpperCase().startsWith(upper)
        );
        this.selectedGroupNumbers = [...this.selectedGroupNumbers, ...matches];
        this.onGroupNumbersChange(this.selectedGroupNumbers);
      }
    },
    applyWildcard(field) {
      if (field === 'carrier') {
        this.wildcardSelect('carrier', this.carrierSearchQuery);
        this.carrierSearchQuery = '';
      } else if (field === 'employer') {
        this.wildcardSelect('employer', this.employerSearchQuery);
        this.employerSearchQuery = '';
      } else if (field === 'groupNumber') {
        this.wildcardSelect('groupNumber', this.groupNumberSearchQuery);
        this.groupNumberSearchQuery = '';
      }
    },
    emitUpdate() {
      this.$emit('input', { ...this.localFilter });
    }
  }
};
</script>

<style scoped lang="scss">
.insurance-filter-card {
  border-left: 3px solid #06b6d4;
}

.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: visible;
}

.filter-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.875rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.filter-card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #334155;

  .filter-icon {
    color: #06b6d4;
    font-size: 0.875rem;
  }
}

.filter-menu ::v-deep .btn-link {
  padding: 0.125rem 0.375rem;
  color: #94a3b8;
  &:hover { color: #475569; }
}

.filter-card-body {
  padding: 0.875rem;
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.375rem;
  display: block;
}

.filter-multiselect ::v-deep {
  .multiselect__tags {
    min-height: 34px;
    padding: 4px 40px 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.8125rem;
  }
  .multiselect__tag {
    background: #cffafe;
    color: #155e75;
    border-radius: 4px;
    font-size: 0.75rem;
    padding: 2px 22px 2px 8px;
    margin-bottom: 2px;
  }
  .multiselect__tag-icon {
    &:after { color: #06b6d4; }
    &:hover { background: #a5f3fc; }
  }
  .multiselect__option--highlight {
    background: #06b6d4;
  }
  .multiselect__content-wrapper {
    z-index: 1060;
    border-radius: 6px;
    border-color: #e2e8f0;
    max-height: 200px;
  }
  .multiselect__placeholder {
    font-size: 0.8125rem;
    color: #94a3b8;
    padding-top: 0;
    margin-bottom: 0;
  }
}

.wildcard-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  margin: 0.375rem 0.5rem;
  padding: 0.4rem 0.75rem;
  background: #06b6d4;
  color: #fff;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s;
  box-shadow: 0 2px 6px rgba(6, 182, 212, 0.35);
  position: sticky;
  top: 0;
  z-index: 2;

  &:hover {
    background: #0891b2;
    box-shadow: 0 3px 10px rgba(6, 182, 212, 0.45);
  }

  code {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 3px;
    padding: 0 0.3rem;
    font-size: 0.7rem;
    color: #fff;
  }

  i {
    font-size: 0.7rem;
  }
}
</style>
